import { useState, useCallback, useEffect } from 'react';
import { apiClient } from '@/services/api-client';

export interface SaleItem {
  id: string;
  photoid: string;
  unitprice: number;
  quantity: number;
  photo?: {
    id: string;
    filename: string;
    original_filename: string;
    storageurl: string;
  };
  event?: {
    id: string;
    title: string;
  };
}

export interface Sale {
  id: string;
  ordernumber: number;
  createdat: string;
  status: string;
  billingStatus: string;
  buyer: {
    id: string;
    name: string;
    email: string;
  } | null;
  totalAmount: number;
  items: SaleItem[];
}

interface UsePhotographerSalesReturn {
  sales: Sale[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  fetchSales: (photographerId: string, billingStatus?: string, limit?: number, offset?: number) => Promise<void>;
  loadMore: (photographerId: string, currentOffset: number, billingStatus?: string, increment?: number) => Promise<void>;
  confirmPaymentReceived: (orderId: string, photographerId: string) => Promise<void>;
  refreshSales: () => Promise<void>;
}

export function usePhotographerSales(photographerId?: string): UsePhotographerSalesReturn {
  const [sales, setSales] = useState<Sale[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentPhotographerId, setCurrentPhotographerId] = useState<string | undefined>(photographerId);
  const [currentStatus, setCurrentStatus] = useState<string | undefined>(undefined);
  const [totalCount, setTotalCount] = useState<number>(0);

  const fetchSales = useCallback(async (photographerId: string, billingStatus?: string, limit?: number, offset?: number) => {
    try {
      setIsLoading(true);
      setError(null);
      setCurrentPhotographerId(photographerId);
      setCurrentStatus(billingStatus);

      const params = new URLSearchParams();
      if (billingStatus) params.append('billingStatus', billingStatus);
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());

      const url = `/orders/photographer/${photographerId}${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await apiClient.get(url);
      
      if (response.data.success) {
        // Garantir que os valores numéricos sejam tratados corretamente
        const processedSales = (response.data.data || []).map((sale: any) => ({
          ...sale,
          billingStatus: sale.billingStatus || 'Pendente',
          totalAmount: typeof sale.totalAmount === 'string' 
            ? parseFloat(sale.totalAmount) 
            : sale.totalAmount || 0,
          items: (sale.items || []).map((item: any) => ({
            ...item,
            unitprice: typeof item.unitprice === 'string' 
              ? parseFloat(item.unitprice) 
              : item.unitprice || 0,
            quantity: typeof item.quantity === 'string' 
              ? parseInt(item.quantity) 
              : item.quantity || 1,
          })),
        }));
        
        // Se offset for 0 ou undefined, substituir a lista; caso contrário, adicionar
        const total = response.data.total || 0;
        setTotalCount(total);
        
        if (offset === undefined || offset === 0) {
          setSales(processedSales);
          // Verificar se há mais itens quando substitui a lista
          // Se não temos total do backend, assumir que há mais se retornou exatamente o limit
          if (total > 0) {
            setHasMore(processedSales.length < total);
          } else {
            // Se não temos total, assumir que há mais se retornou exatamente o limit solicitado
            setHasMore(limit ? processedSales.length === limit : false);
          }
        } else {
          // Quando adiciona mais itens, usar função de atualização para acessar o estado anterior
          setSales(prev => {
            const newSales = [...prev, ...processedSales];
            // Se não temos total do backend, assumir que há mais se retornou exatamente o increment
            if (total > 0) {
              setHasMore(newSales.length < total);
            } else {
              setHasMore(processedSales.length === (limit || 5));
            }
            return newSales;
          });
        }
      } else {
        setError(response.data.message || 'Erro ao carregar vendas');
        setSales([]);
        setHasMore(false);
      }
    } catch (err: any) {
      console.error('Erro ao buscar vendas:', err);
      // Tentar extrair mensagem de erro de diferentes formatos
      let errorMessage = 'Erro ao carregar vendas';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setError(errorMessage);
      setSales([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async (photographerId: string, currentOffset: number, billingStatus?: string, increment: number = 5) => {
    await fetchSales(photographerId, billingStatus, increment, currentOffset);
  }, [fetchSales]);

  const confirmPaymentReceived = useCallback(async (orderId: string, photographerId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.post(`/orders/${orderId}/photographer/${photographerId}/confirm-payment`);

      if (response.data.success) {
        // Atualizar a venda na lista local
        setSales(prevSales =>
          prevSales.map(sale =>
            sale.id === orderId
              ? { ...sale, billingStatus: 'Ok' }
              : sale
          )
        );
      } else {
        throw new Error(response.data.message || 'Erro ao confirmar recebimento');
      }
    } catch (err: any) {
      console.error('Erro ao confirmar recebimento:', err);
      setError(err.response?.data?.message || 'Erro ao confirmar recebimento');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshSales = useCallback(async () => {
    if (currentPhotographerId) {
      await fetchSales(currentPhotographerId, currentStatus);
    }
  }, [currentPhotographerId, currentStatus, fetchSales]);

  // Carregar vendas automaticamente se photographerId for fornecido
  // Por padrão, carregar apenas 5 vendas pendentes (aba padrão)
  useEffect(() => {
    if (photographerId) {
      fetchSales(photographerId, 'Pendente', 5, 0);
    }
  }, [photographerId]); // Remover fetchSales da dependência para evitar loop

  return {
    sales,
    isLoading,
    error,
    hasMore,
    fetchSales,
    loadMore,
    confirmPaymentReceived,
    refreshSales,
  };
}

